import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { PAYMENT_GAS, refundPercentOf } from '@78/core';
import { fetchDeal } from '../lib/api.ts';
import { getTonBalance } from '../lib/chain.ts';
import { Sheet } from '../components/Sheet.tsx';
import { lastWalletRequest, useActions } from '../lib/actions.ts';
import { GasNote } from '../components/GasNote.tsx';
import { resolveInvitation } from '../lib/referral.ts';
import { useErrorText } from '../lib/errors.ts';
import { reportFailure } from '../lib/report.ts';
import { shortAddress } from '../lib/collections.ts';
import { useFmt } from '../lib/format.tsx';


/** Сколько ждать сеть, прежде чем спрашивать индексер заново. */
const NETWORK_WAIT_MS = 6000;

/**
 * Карточка оффера — раскладка макета.
 *
 * Порядок блоков не косметика: сначала сумма, которую платят сегодня, потом
 * график, и только потом — что будет при срыве. Человек решает по первому
 * экрану, и последствия должны быть на нём же, а не за прокруткой.
 */
export function Offer({ address, onGuide }: { address: string; onGuide?: (() => void) | undefined }) {
  const { t } = useTranslation();
  const { sum, money, dateTime, duration } = useFmt();
  const actions = useActions();
  const errorText = useErrorText();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  // Баланс нужен ровно здесь: человек должен видеть, что останется после
  // списания, до того как подтвердит, а не после отказа кошелька.
  const balance = useQuery({
    queryKey: ['tonBalance', actions.myAddress],
    queryFn: () => getTonBalance(actions.myAddress),
    enabled: actions.connected,
    staleTime: 30_000,
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['deal', address],
    queryFn: () => fetchDeal(address),
  });

  if (isLoading) {
    return (
      <div className="screen">
        <div className="skeleton" style={{ minHeight: 196, borderRadius: 0 }} />
        <div className="skeleton" style={{ minHeight: 120 }} />
        <div className="skeleton" style={{ minHeight: 180 }} />
      </div>
    );
  }
  if (error !== null && error !== undefined) {
    return <div className="screen"><div className="card card-dng text">{errorText(error)}</div></div>;
  }
  if (data === undefined) return null;

  const pct = String(refundPercentOf(data.terms.refundTier));
  const weeks = Math.round(data.terms.period / (7 * 86400));
  const installment =
    (BigInt(data.terms.totalPrice) - BigInt(data.terms.upfront)) / BigInt(data.terms.nPayments);
  const last = data.schedule[data.schedule.length - 1];
  const now = Math.floor(Date.now() / 1000);
  // Сколько на самом деле уйдёт с кошелька. Взнос едет значением сообщения,
  // надбавка на обработку — сверху; контракт вычтет ровно её, но кошелёк
  // спросит обе. Экран сравнивал баланс с одним взносом и обещал «хватит»
  // там, где кошелёк отказывал: между 0,10 и 0,12 человек видел остаток и
  // отказ одновременно, а понять причину было неоткуда.
  const charged = BigInt(data.terms.upfront) + PAYMENT_GAS;

  const buy = () => {
    setBusy(true);
    setErr(null);
    // Приглашение спрашивается здесь, а не при открытии экрана: пара
    // «приглашающий — покупатель» существует только вместе с кошельком, а до
    // нажатия кнопки он может быть и не подключён. Отказ ничего не ломает —
    // покупка просто идёт как обычная.
    void resolveInvitation(actions.myAddress)
      .then((invitation) =>
        actions.pay({
          dealAddress: data.address,
          terms: {
            totalPrice: BigInt(data.terms.totalPrice),
            upfront: BigInt(data.terms.upfront),
            deferFee: BigInt(data.terms.deferFee),
            period: data.terms.period,
            nPayments: data.terms.nPayments,
            refundTier: data.terms.refundTier,
            maxDeferrals: data.terms.maxDeferrals,
            feeBps: data.terms.feeBps,
            deadline: data.terms.deadline,
          },
          startTs: data.progress.startTs,
          exactAmount: BigInt(data.terms.upfront),
          ...(invitation === undefined ? {} : { referral: invitation }),
        }),
      )
      .then(() => {
        setWaiting(true);
        setTimeout(() => {
          void qc.invalidateQueries({ queryKey: ['deal', address] });
          setWaiting(false);
        }, NETWORK_WAIT_MS);
      })
      .catch((e: unknown) => {
        // Покупка — единственный путь, о сбоях которого мы не узнавали
        // вовсе: отправка идёт мимо сервера, а отчёт отсюда не уходил.
        // Человек говорил «не вышло», и в журнале не было ни строки.
        const have = balance.data == null ? 'баланс неизвестен' : `баланс ${balance.data}`;
        reportFailure('покупка', e, `${lastWalletRequest()} · ${have} · нужно ${charged}`);
        const text = errorText(e);
        if (text.length > 0) setErr(text);
      })
      .finally(() => setBusy(false));
  };

  return (
    <>
      <div className="screen" style={{ padding: 0, gap: 0 }}>
        {/* Заглавный блок: место под изображение гифта и уровень возврата
            крупной меткой — он определяет риск, и прятать его нельзя. */}
        <div
          style={{
            height: 196,
            backgroundColor: 'var(--sf2)',
            // Картинка гифта, если она есть; штриховка — запасной вариант, у
            // части айтемов изображения нет вовсе.
            ...(data.nftImage === null
              ? {
                  backgroundImage:
                    'repeating-linear-gradient(135deg,transparent 0 9px,var(--ph) 9px 10px)',
                }
              : {
                  backgroundImage: `url(${data.nftImage})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }),
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            padding: '12px 14px',
          }}
        >
          <span className="label" style={{ fontSize: 8, letterSpacing: '0.14em', color: 'var(--ink2)' }}>
            GIFT ART · 1:1
          </span>
          <span
            className="chip"
            style={{ background: 'var(--lime)', color: 'var(--lime-ink)', fontSize: 9, padding: '5px 8px', borderRadius: 7 }}
          >
            {t('market.refund_short')} {pct}%
          </span>
        </div>

        <div className="col" style={{ padding: '16px 14px 26px', gap: 14 }}>
          <div>
            <div style={{ fontSize: 23, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.15 }}>
              {data.nftName ?? shortAddress(data.nftAddress)}
            </div>
            <div className="row" style={{ justifyContent: 'flex-start', gap: 8, alignItems: 'baseline', marginTop: 5 }}>
              <span className="mono muted" style={{ fontSize: 12 }}>
                {sum(data.terms.totalPrice)} {t('market.total_word')}
              </span>
            </div>
          </div>

          {/* Главная сумма — лаймовым блоком: это единственное число, ради
              которого человек открыл экран. */}
          <div className="card card-lime" style={{ background: 'var(--lime)', color: 'var(--lime-ink)', padding: 16 }}>
            <div className="mono" style={{ fontSize: 8.5, letterSpacing: '0.16em', opacity: 0.7 }}>
              {t('offer.pay_today')}
            </div>
            <div
              className="mono num"
              style={{ fontSize: 38, fontWeight: 700, letterSpacing: '-0.03em', lineHeight: 1.1, marginTop: 6 }}
            >
              {money(data.terms.upfront)}
            </div>
            <div style={{ fontSize: 12.5, fontWeight: 700, marginTop: 2 }}>
              {/* Доля взноса считается из условий сделки: в макете стояло
                  «30% цены», и это число ехало людям с любой сделкой. */}
              {t('offer.upfront_share', {
                pct: Math.round(
                  (Number(BigInt(data.terms.upfront) / 1000n) * 100) /
                    Number(BigInt(data.terms.totalPrice) / 1000n),
                ),
              })}
            </div>
          </div>

          {/* График столбиками: видно и суммы, и сколько шагов осталось. */}
          <div className="card col" style={{ gap: 12 }}>
            <div className="row" style={{ alignItems: 'baseline' }}>
              <span style={{ fontSize: 15, fontWeight: 800, letterSpacing: '-0.01em' }}>
                {t('offer.payment_schedule')}
              </span>
              <span className="label">SCHEDULE</span>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              {data.schedule.map((p) => {
                const paid = p.safeUntil !== null && p.safeUntil < now;
                return (
                  <div key={p.step} className="col" style={{ flex: 1, gap: 6 }}>
                    <div
                      style={{
                        height: 5,
                        borderRadius: 3,
                        background: paid ? 'var(--lime)' : 'var(--line)',
                      }}
                    />
                    <div
                      className="mono num"
                      style={{ fontSize: 10, fontWeight: 700, color: paid ? 'var(--ink)' : 'var(--ink3)' }}
                    >
                      {money(p.step === 0 ? data.terms.upfront : installment)}
                    </div>
                    <div className="mono dim" style={{ fontSize: 8 }}>
                      {p.dueAt === null ? '—' : dateTime(p.dueAt).split(',')[0]}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="text">
              {data.terms.nPayments} × {money(installment)} · {weeks} {t('market.weeks_short')}
              {last?.safeUntil != null && ` · ${t('buyer.safe_pay_until').toLowerCase()} ${dateTime(last.safeUntil)}`}
            </div>
          </div>

          {/* Обязательный блок: §2 требует показывать последствия суммой, а
              не процентом. «Вернётся 25%» никто не считает в уме. */}
          <div className="card col" style={{ gap: 12 }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 800, letterSpacing: '-0.01em' }}>
                {t('offer.if_stop_paying')}
              </div>
              <div className="label" style={{ marginTop: 3 }}>LOSS BY STEP · ALWAYS VISIBLE</div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1.15fr .95fr .95fr', gap: '2px 8px', alignItems: 'center' }}>
              <div className="label label-xs" style={{ paddingBottom: 6 }}>{t('offer.point')}</div>
              <div className="label label-xs" style={{ textAlign: 'right', paddingBottom: 6 }}>{t('offer.get_back')}</div>
              <div className="label label-xs" style={{ textAlign: 'right', paddingBottom: 6 }}>{t('offer.lose')}</div>

              {data.forfeitTable.map((r) => (
                <div key={r.step} style={{ display: 'contents' }}>
                  <div style={{ fontSize: 12, fontWeight: 600, padding: '8px 0', borderTop: '1px solid var(--line)' }}>
                    {r.step === 0 ? t('counter.down_payment_only') : `+ ${r.step}`}
                  </div>
                  <div className="mono num lime" style={{ fontSize: 12.5, fontWeight: 700, textAlign: 'right', padding: '8px 0', borderTop: '1px solid var(--line)' }}>
                    {money(r.refunded)}
                  </div>
                  <div className="mono num danger" style={{ fontSize: 12.5, fontWeight: 700, textAlign: 'right', padding: '8px 0', borderTop: '1px solid var(--line)' }}>
                    {money(r.lost)}
                  </div>
                </div>
              ))}
            </div>

            <div className="dim" style={{ fontSize: 11.5, lineHeight: 1.5 }}>
              {t('offer.upfront_never_back', {
                upfront: money(data.terms.upfront),
                refund: pct,
              })}
            </div>
          </div>

          <div className="card col" style={{ gap: 10 }}>
            <div style={{ fontSize: 15, fontWeight: 800, letterSpacing: '-0.01em' }}>{t('offer.terms')}</div>
            {[
              [t('builder.refund_level'), `${pct}%`, t('builder.what_buyer_gets_back')],
              [t('market.grace_period'), duration(data.terms.graceSec), t('market.how_late_may_after')],
              [
                t('market.deferrals'),
                data.terms.maxDeferrals === 0
                  ? t('builder.none')
                  : `${data.terms.maxDeferrals} · ${money(data.terms.deferFee)}`,
                t('market.each_shifts_whole_schedule'),
              ],
              [t('deal.service_fee'), `${data.terms.feeBps / 100}%`, t('market.hard_coded_contract_never')],
            ].map(([k, v, note]) => (
              <div key={k} className="row" style={{ alignItems: 'flex-start', paddingTop: 10, borderTop: '1px solid var(--line)' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700 }}>{k}</div>
                  <div className="dim" style={{ fontSize: 11, marginTop: 2, lineHeight: 1.45 }}>{note}</div>
                </div>
                <div className="mono num" style={{ fontSize: 12.5, fontWeight: 700, textAlign: 'right', flex: '0 0 auto' }}>
                  {v}
                </div>
              </div>
            ))}
          </div>

          {/* Ссылка на гайд пунктиром — приём макета. Вопрос «где лежит
              гифт» задают до оплаты, и ответ должен быть на этом же экране. */}
          {onGuide !== undefined && (
            <button
              onClick={onGuide}
              className="row"
              style={{
                textAlign: 'left',
                gap: 11,
                padding: '13px 14px',
                borderRadius: 16,
                border: '1px dashed var(--line)',
                background: 'transparent',
                cursor: 'pointer',
                color: 'var(--ink)',
                font: 'inherit',
              }}
            >
              <span
                className="mono lime"
                style={{
                  flex: '0 0 34px',
                  height: 34,
                  borderRadius: 10,
                  background: 'var(--sf2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 13,
                }}
              >
                ?
              </span>
              <span style={{ flex: 1 }}>
                <span style={{ display: 'block', fontSize: 12.5, fontWeight: 700 }}>
                  {t('offer.where_gift_sits_until')}
                </span>
                <span className="dim" style={{ display: 'block', fontSize: 11, marginTop: 2 }}>
                  {t('offer.contract_not_wallet_here')}
                </span>
              </span>
              <span className="dim" style={{ fontSize: 15 }}>›</span>
            </button>
          )}

          {err !== null && <div className="card card-dng text">{err}</div>}
          {waiting && (
            <div className="card col">
              <span className="label lime">{t('state.sent_waiting')}</span>
              <span className="text">{t('state.sent_waiting_hint')}</span>
            </div>
          )}

          {/* Надбавка сверх самой суммы — до нажатия, а не после. */}
          {data.state === 1 && <GasNote kind="pay" />}
        </div>
      </div>

      {/* Подтверждение перед списанием — экран из макета. Раньше нажатие
          сразу отправляло транзакцию, и единственным местом, где человек
          видел сумму, было окно кошелька: без остатка на балансе, без того,
          что он потеряет при срыве, и без слова о том, что средства уходят
          в контракт, а не нам. */}
      {confirming && data.state === 1 && (
        <Sheet
          title={t('confirm.upfront_title')}
          confirmLabel={`${t('action.pay')} · ${money(data.terms.upfront)}`}
          busy={busy}
          /* Неизвестный баланс не повод запрещать: он не читается, когда
             TON API отвечает отказом, и тогда единственный судья — кошелёк. */
          disabled={balance.data != null && balance.data < charged}
          onClose={() => setConfirming(false)}
          onConfirm={() => {
            setConfirming(false);
            buy();
          }}
        >
          <div className="label">{t('confirm.charged_now')}</div>
          <div
            className="mono num"
            style={{ fontSize: 44, fontWeight: 700, letterSpacing: '-0.03em', lineHeight: 1.05 }}
          >
            {money(data.terms.upfront)}
          </div>
          <span className="muted" style={{ fontSize: 12.5 }}>
            {balance.data == null
              ? t('confirm.balance_unknown')
              : t('confirm.balance_after', {
                  rest: money(balance.data > charged ? balance.data - charged : 0n),
                })}
          </span>
          {balance.data != null && balance.data < charged && (
            <span className="danger" style={{ fontSize: 12.5 }}>{t('confirm.not_enough')}</span>
          )}

          {/* Что будет при срыве — здесь, а не мелким шрифтом ниже: это
              единственное, о чём человек потом пожалеет. */}
          {data.forfeitTable[1] !== undefined && (
            <span className="text" style={{ fontSize: 12.5 }}>
              {t('confirm.if_quit', {
                back: money(data.forfeitTable[1].refunded),
                paid: money(data.forfeitTable[1].paidAfterStep),
              })}
            </span>
          )}

          <span className="dim" style={{ fontSize: 11 }}>{t('confirm.legal')}</span>
        </Sheet>
      )}

      {/* Главное действие — отдельной панелью внизу, как в макете: оно
          должно быть под пальцем на любой прокрутке. */}
      {data.state === 1 && (
        <div className="mainbar">
          <button
            className="btn"
            disabled={busy || !actions.connected}
            onClick={() => setConfirming(true)}
          >
            {busy ? '…' : `${t('action.buy')} · ${sum(data.terms.upfront)}`}
          </button>
        </div>
      )}
    </>
  );
}
